import { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import EditIcon from "@mui/icons-material/Edit";
import PaidIcon from "@mui/icons-material/Paid";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import {
  getRbacUsers,
  getRoles,
  forceLogoutUser,
  forceLogoutAllUsers,
  updateUserSubscriptionPlan,
  bulkUpdateUserSubscriptionPlan,
} from "../../../Api/RbacAdminApi";
import CODE from "../../../Static/Constants/StatusCodes";
import UserEditDialog from "./UserEditDialog";
import CreateUserDialog from "./CreateUserDialog";
import { useToast } from "../../../Hooks/useToast";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import SearchInput from "../../Overview/components/SearchInput";
import { CanCreate, CanUpdate } from "../../../Components/PermissionGuards";
import { usePermissions } from "../../../Context/PermissionContext";
import { FEATURE_CODES } from "../../../Static/FeatureMapping";
import DownloadCSV from "../../../Helper/CSVOperations/DownloadCSV";
import NameTheCSV from "../../../Helper/CSVOperations/NameTheCSV";
import AppCache from "../../../Helper/Common/AppCache";

const PRIMARY_PURPLE = "#4D44D8";
const DEFAULT_PER_PAGE = 20;
const ROLE_FILTER_KEY = "roleCodes";

// Column visibility is a per-admin preference, so it lives in localStorage rather
// than in the URL or on the server. Both halves are stored: `selected` is the
// choice, `known` is every column that existed when the choice was made, so a
// column that appears later (a newly configured location level) can be told apart
// from one the admin deliberately hid.
const COLUMNS_CACHE_KEY = "ManageUsersTableColumns";

const ACCESS_ALL = "All";
const ACCESS_NONE = "—";

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ").filter(Boolean);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

// orgLocationLevel stores the type as the org configured it ("depot", "Business
// Zone"), which goes straight into a column header.
export const toLevelLabel = (type) =>
  String(type || "")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// One access dimension rendered as text. An unrestricted grant reads "All"
// instead of enumerating every value; no grant reads as a dash. The same string
// is what goes into the CSV, so the export and the screen cannot drift.
export const accessText = (hasAllAccess, values) => {
  if (hasAllAccess) return ACCESS_ALL;
  const list = (values || []).filter(Boolean);
  return list.length ? list.join(", ") : ACCESS_NONE;
};

// The count counterpart of accessText, for the two "Number of ..." columns. An
// unrestricted grant reads "All" there too, since no finite number describes it.
// Otherwise the bare number: the header already says what is being counted, and
// a plain figure is what makes two rows comparable at a glance.
export const accessCount = (hasAllAccess, values) =>
  hasAllAccess ? ACCESS_ALL : (values || []).length;

export const isSameLevels = (a, b) =>
  (a || []).length === (b || []).length &&
  (a || []).every(
    (level, index) =>
      level?.level === b[index]?.level && level?.type === b[index]?.type
  );

export const locationLevelText = (user, levelKey) => {
  const levelAccess = user.locationLevelAccess;
  if (levelAccess?.hasAllAccess) return ACCESS_ALL;
  const entry = (levelAccess?.levels || []).find((l) => l.level === levelKey);
  return accessText(false, entry?.values);
};

// A list cell: one line, ellipsised, with the whole list on hover. "All" and the
// dash never overflow, so they are rendered without a tooltip.
const AccessListCell = ({ text }) => {
  const cell = (
    <Typography
      sx={{
        fontSize: 15,
        color: "#444",
        maxWidth: 200,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
      }}
    >
      {text}
    </Typography>
  );

  if (text === ACCESS_ALL || text === ACCESS_NONE) return cell;

  return (
    <Tooltip
      title={text}
      placement="bottom-start"
      slotProps={{
        tooltip: {
          sx: { fontSize: 12, lineHeight: 1.6, maxWidth: 520, p: "8px 12px" },
        },
      }}
    >
      {cell}
    </Tooltip>
  );
};

// Seed the role filter from the URL (e.g. deep-linked from a role's "View Users")
// — `?roleCodes=ORG_ADMIN,DEPOT_INCHARGE`.
const getInitialFilters = () => {
  const codes = new URLSearchParams(window.location.search)
    .get(ROLE_FILTER_KEY);
  if (!codes) return {};
  const list = codes.split(",").map((c) => c.trim()).filter(Boolean);
  return list.length ? { [ROLE_FILTER_KEY]: list } : {};
};

const UserAssignmentsTab = () => {
  const [users, setUsers] = useState([]);
  // The org's configured location hierarchy, straight off the list response. One
  // column is rendered per level, so an org with a Zone level gets a Zone column
  // without a frontend change.
  const [locationLevels, setLocationLevels] = useState([]);
  // null until the stored preference has been read; see visibleColumns.
  const [selectedColumns, setSelectedColumns] = useState(null);
  const [total, setTotal] = useState(0);
  const [baseTotal, setBaseTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(DEFAULT_PER_PAGE);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [roles, setRoles] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(getInitialFilters);
  const [loading, setLoading] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [logoutUser, setLogoutUser] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutAllOpen, setLogoutAllOpen] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // Subscription plan controls: bulk selection + confirm dialogs.
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [planChange, setPlanChange] = useState(null); // { user, plan } for single
  const [bulkPlan, setBulkPlan] = useState(null); // "paid" | "free" for bulk
  const [savingPlan, setSavingPlan] = useState(false);
  const { openNotification } = useToast();
  const { hasPermission } = usePermissions();
  const debounceTimer = useRef(null);

  // Force Logout / Edit access are both 'update' operations. Returns true for
  // RBAC v1 orgs (no per-feature gating), so the column shows as before there.
  const canUpdate = hasPermission(FEATURE_CODES.ACCESS_MANAGEMENT, "update");

  const selectedRoleCodes = selectedFilters[ROLE_FILTER_KEY] || [];

  const fetchUsers = (params) => {
    setLoading(true);
    getRbacUsers(params)
      .then((res) => {
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          const data = res.response?.data || {};
          setUsers(data.users || []);
          setTotal(data.total || 0);
          // Sent on every page, including an empty one, so the columns do not
          // collapse when a search matches nobody. The hierarchy is the same on
          // every response, so keep the previous array when it has not changed:
          // a fresh identity here would rebuild the column list and throw away
          // the admin's drag-reordering on each refetch.
          setLocationLevels((prev) => {
            const next = data.locationLevels || [];
            return isSameLevels(prev, next) ? prev : next;
          });
        }
      })
      .finally(() => setLoading(false));
  };

  // Build the query params consistently for every fetch (refresh after edits etc.).
  const buildParams = () => ({
    page,
    perPage,
    ...(search ? { search } : {}),
    ...(selectedRoleCodes.length ? { [ROLE_FILTER_KEY]: selectedRoleCodes.join(",") } : {}),
  });

  // Grand total of users, independent of the role filter / search. Drives the
  // header count badge so it stays constant while filters narrow the results.
  const fetchBaseTotal = () => {
    getRbacUsers({ page: 1, perPage }).then((res) => {
      if (res?.responseStatus?.code === CODE.SUCCESS) {
        setBaseTotal(res.response?.data?.total || 0);
      }
    });
  };

  useEffect(() => {
    fetchUsers(buildParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, search, selectedFilters]);

  useEffect(() => {
    fetchBaseTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Roles populate the Role filter dropdown.
  useEffect(() => {
    getRoles().then((res) => {
      if (res?.responseStatus?.code === CODE.SUCCESS) {
        setRoles(res.response?.data || res.response || []);
      }
    });
  }, []);

  const availableFilters = useMemo(() => {
    if (!roles.length) return {};
    return {
      [ROLE_FILTER_KEY]: {
        key: ROLE_FILTER_KEY,
        label: "Role",
        displayType: "multiSelectDropdown",
        values: roles.map((r) => ({ id: r.roleCode, value: r.roleName })),
      },
    };
  }, [roles]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setPage(1);
      setSearch(value);
    }, 400);
  };

  const applyFilters = (filters) => {
    setPage(1);
    setSelectedFilters(filters);
  };

  const handleForceLogout = () => {
    setLoggingOut(true);
    forceLogoutUser(logoutUser.userId)
      .then((res) => {
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          openNotification({ text: `${logoutUser.name} has been logged out successfully`, icon: "toast-success" });
        } else {
          openNotification({ text: "Failed to log out user. Please try again.", icon: "toast-error" });
        }
      })
      .catch(() => openNotification({ text: "Failed to log out user. Please try again.", icon: "toast-error" }))
      .finally(() => {
        setLoggingOut(false);
        setLogoutUser(null);
      });
  };

  const handleForceLogoutAll = () => {
    setLoggingOutAll(true);
    forceLogoutAllUsers()
      .then((res) => {
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          openNotification({ text: "All users have been logged out successfully", icon: "toast-success" });
        } else {
          openNotification({ text: "Failed to log out all users. Please try again.", icon: "toast-error" });
        }
      })
      .catch(() => openNotification({ text: "Failed to log out all users. Please try again.", icon: "toast-error" }))
      .finally(() => {
        setLoggingOutAll(false);
        setLogoutAllOpen(false);
      });
  };

  // Change a single user's subscription plan (paid <-> free).
  const handleUpdatePlan = () => {
    if (!planChange) return;
    setSavingPlan(true);
    updateUserSubscriptionPlan(planChange.user.userId, planChange.plan)
      .then((res) => {
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          openNotification({ text: `${planChange.user.name} is now on the ${planChange.plan === "paid" ? "Paid" : "Free"} plan`, icon: "toast-success" });
          fetchUsers(buildParams());
        } else {
          openNotification({ text: "Failed to update plan. Please try again.", icon: "toast-error" });
        }
      })
      .catch(() => openNotification({ text: "Failed to update plan. Please try again.", icon: "toast-error" }))
      .finally(() => {
        setSavingPlan(false);
        setPlanChange(null);
      });
  };

  // Change the plan for all currently selected users at once.
  const handleBulkUpdatePlan = () => {
    if (!bulkPlan || !selectedRowKeys.length) return;
    setSavingPlan(true);
    bulkUpdateUserSubscriptionPlan(selectedRowKeys, bulkPlan)
      .then((res) => {
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          openNotification({ text: `${selectedRowKeys.length} user(s) set to the ${bulkPlan === "paid" ? "Paid" : "Free"} plan`, icon: "toast-success" });
          setSelectedRowKeys([]);
          fetchUsers(buildParams());
        } else {
          openNotification({ text: "Failed to update plans. Please try again.", icon: "toast-error" });
        }
      })
      .catch(() => openNotification({ text: "Failed to update plans. Please try again.", icon: "toast-error" }))
      .finally(() => {
        setSavingPlan(false);
        setBulkPlan(null);
      });
  };

  // Export ALL users across every page as CSV, honouring the currently applied
  // search text and role filter (not just the page on screen). Pages through the
  // server with a larger perPage until the accumulated count reaches `total`.
  const handleDownloadUsers = async () => {
    setIsDownloading(true);
    try {
      const DOWNLOAD_PER_PAGE = 100;
      const MAX_PAGES = 1000; // safety cap against infinite loops
      const baseParams = {
        ...(search ? { search } : {}),
        ...(selectedRoleCodes.length
          ? { [ROLE_FILTER_KEY]: selectedRoleCodes.join(",") }
          : {}),
      };

      const collected = [];
      let currentPage = 1;
      let totalCount = Infinity;

      while (currentPage <= MAX_PAGES && collected.length < totalCount) {
        // eslint-disable-next-line no-await-in-loop
        const res = await getRbacUsers({
          ...baseParams,
          page: currentPage,
          perPage: DOWNLOAD_PER_PAGE,
        });
        if (res?.responseStatus?.code !== CODE.SUCCESS) break;
        const data = res.response?.data || {};
        const batch = data.users || [];
        totalCount = data.total || 0;
        if (!batch.length) break;
        collected.push(...batch);
        currentPage += 1;
      }

      if (!collected.length) return;

      // Exactly the columns on screen, in the same order, and with the full
      // untruncated list in every cell — the table ellipsises, the CSV does not.
      // DownloadCSV quotes values containing commas, so the joined lists survive.
      const exportFields = visibleColumns.flatMap((col) => col.exportColumns || []);
      const rows = collected.map((user) =>
        Object.fromEntries(exportFields.map((field) => [field.header, field.value(user)]))
      );

      DownloadCSV(NameTheCSV("AccessManagement", "ManageUsers"), rows);
    } catch (err) {
      console.error("Failed to download users", err);
      openNotification({ text: "Failed to download users. Please try again.", icon: "toast-error" });
    } finally {
      setIsDownloading(false);
    }
  };

  // Ant Design column config. Custom render functions keep the original visual
  // design (gradient avatar, role/status chips, action buttons).
  //
  // Two extras beyond the Ant Design contract:
  //   headerLabel  - what the column picker lists the column as.
  //   exportColumns - the CSV fields this column contributes. The picker drives
  //                   both the table and the download from this one definition,
  //                   so a hidden column cannot silently reappear in the export.
  //                   The User column contributes two fields (name and email).
  const columns = useMemo(
    () => [
      {
        key: "user",
        dataIndex: "name",
        title: "User",
        sorter: false,
        // Pinned, which also makes it undeselectable in the picker. With up to 14
        // columns the identity has to stay on screen while the access lists scroll,
        // and "Deselect all" must still leave a table that says whose row it is.
        fixed: "left",
        width: 280,
        exportColumns: [
          { header: "User", value: (user) => user.name ?? "" },
          { header: "Email", value: (user) => user.email ?? "" },
        ],
        render: (_value, user) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 42,
                height: 42,
                fontSize: 15,
                fontWeight: 700,
                background: "linear-gradient(135deg, #6B63E8 0%, #4D44D8 100%)",
                flexShrink: 0,
              }}
            >
              {getInitials(user.name)}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#2E2E2E" }}>
                {user.name || "—"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#888", mt: 0.25 }}>
                {user.email || ""}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        key: "role",
        dataIndex: "role",
        title: "Role",
        sorter: false,
        exportColumns: [
          { header: "Role", value: (user) => user.role?.roleName ?? "" },
        ],
        render: (_value, user) =>
          user.role?.roleName ? (
            <Chip
              label={user.role.roleName}
              size="small"
              sx={{
                backgroundColor: "#EEEFFF",
                color: PRIMARY_PURPLE,
                fontWeight: 500,
                fontSize: 13,
                height: 28,
                borderRadius: "6px",
              }}
            />
          ) : (
            <Typography sx={{ fontSize: 14, color: "#BBB" }}>—</Typography>
          ),
      },
      {
        key: "locationAccess",
        dataIndex: "locationAccess",
        title: "Number of Locations",
        sorter: false,
        exportColumns: [
          {
            header: "Number of Locations",
            value: (user) =>
              accessCount(
                user.locationAccess?.hasAllAccess,
                user.locationAccess?.locations
              ),
          },
        ],
        render: (_value, user) => (
          <Typography sx={{ fontSize: 15, color: "#444" }}>
            {accessCount(
              user.locationAccess?.hasAllAccess,
              user.locationAccess?.locations
            )}
          </Typography>
        ),
      },
      {
        key: "fleetAccess",
        dataIndex: "fleetAccess",
        title: "Number of Customer Accounts",
        sorter: false,
        exportColumns: [
          {
            header: "Number of Customer Accounts",
            value: (user) =>
              accessCount(user.fleetAccess?.hasAllAccess, user.fleetAccess?.fleets),
          },
        ],
        render: (_value, user) => (
          <Typography sx={{ fontSize: 15, color: "#444" }}>
            {accessCount(user.fleetAccess?.hasAllAccess, user.fleetAccess?.fleets)}
          </Typography>
        ),
      },
      // One column per configured location level, then the two fleet-derived
      // dimensions. All of them list the granted values rather than a count.
      ...locationLevels.map((level) => {
        const title = `List of ${toLevelLabel(level.type)} Access`;
        return {
          key: `locationLevel_${level.level}`,
          dataIndex: `locationLevel_${level.level}`,
          title,
          sorter: false,
          exportColumns: [
            { header: title, value: (user) => locationLevelText(user, level.level) },
          ],
          render: (_value, user) => (
            <AccessListCell text={locationLevelText(user, level.level)} />
          ),
        };
      }),
      {
        key: "customerAccountsList",
        dataIndex: "customerAccountsList",
        title: "List of Customer Accounts Access",
        sorter: false,
        exportColumns: [
          {
            header: "List of Customer Accounts Access",
            value: (user) =>
              accessText(user.fleetAccess?.hasAllAccess, user.fleetAccess?.fleets),
          },
        ],
        render: (_value, user) => (
          <AccessListCell
            text={accessText(user.fleetAccess?.hasAllAccess, user.fleetAccess?.fleets)}
          />
        ),
      },
      {
        key: "businessSegmentList",
        dataIndex: "businessSegmentList",
        title: "List of Business Segment Access",
        sorter: false,
        exportColumns: [
          {
            header: "List of Business Segment Access",
            value: (user) =>
              accessText(
                user.businessSegmentAccess?.hasAllAccess,
                user.businessSegmentAccess?.segments
              ),
          },
        ],
        render: (_value, user) => (
          <AccessListCell
            text={accessText(
              user.businessSegmentAccess?.hasAllAccess,
              user.businessSegmentAccess?.segments
            )}
          />
        ),
      },
      {
        key: "status",
        dataIndex: "isActive",
        title: "Status",
        sorter: false,
        exportColumns: [
          {
            header: "Status",
            value: (user) => (user.isActive !== false ? "Active" : "Inactive"),
          },
        ],
        render: (_value, user) => (
          <Chip
            label={user.isActive !== false ? "Active" : "Inactive"}
            size="small"
            sx={{
              backgroundColor: user.isActive !== false ? "#F0FFF4" : "#FFF0F0",
              color: user.isActive !== false ? "#166534" : "#991B1B",
              fontWeight: 600,
              fontSize: 13,
              height: 26,
              borderRadius: "6px",
            }}
          />
        ),
      },
      {
        key: "subscriptionPlan",
        dataIndex: "subscriptionPlan",
        title: "Plan",
        sorter: false,
        exportColumns: [
          {
            header: "Plan",
            value: (user) =>
              (user.subscriptionPlan || "paid") === "paid" ? "Paid" : "Free",
          },
        ],
        render: (_value, user) => {
          const isPaid = (user.subscriptionPlan || "paid") === "paid";
          return (
            <Chip
              label={isPaid ? "Paid" : "Free"}
              size="small"
              sx={{
                backgroundColor: isPaid ? "#EEEFFF" : "#F3F4F6",
                color: isPaid ? PRIMARY_PURPLE : "#4B5563",
                fontWeight: 600,
                fontSize: 13,
                height: 26,
                borderRadius: "6px",
              }}
            />
          );
        },
      },
      // Actions column is only present when the user can update (Force Logout /
      // Edit access / change plan). Hidden entirely otherwise — see the
      // `canUpdate` filter below.
      {
        key: "actions",
        dataIndex: "actions",
        title: "Actions",
        align: "right",
        fixed: "right",
        width: 120,
        sorter: false,
        render: (_value, user) => {
          const isPaid = (user.subscriptionPlan || "paid") === "paid";
          return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
            <Tooltip title={isPaid ? "Set to Free plan" : "Set to Paid plan"} componentsProps={{ tooltip: { sx: { fontSize: "10px" } } }}>
              <IconButton
                size="small"
                onClick={() => setPlanChange({ user, plan: isPaid ? "free" : "paid" })}
                data-testid="toggle-plan-btn"
                sx={{ color: "#6B7280", padding: "6px", "&:hover": { backgroundColor: "#F3F4F6" } }}
              >
                {isPaid ? <MoneyOffIcon sx={{ fontSize: 20 }} /> : <PaidIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Force Logout" componentsProps={{ tooltip: { sx: { fontSize: "10px" } } }}>
              <IconButton
                size="small"
                onClick={() => setLogoutUser(user)}
                data-testid="force-logout-user-btn"
                sx={{ color: "#FF6364", padding: "6px", "&:hover": { backgroundColor: "#FFF0F0" } }}
              >
                <LogoutIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit access" componentsProps={{ tooltip: { sx: { fontSize: "10px" } } }}>
              <IconButton
                size="small"
                onClick={() => setEditUser(user)}
                data-testid="edit-access-btn"
                sx={{ color: PRIMARY_PURPLE, padding: "6px", "&:hover": { backgroundColor: "#EEEFFF" } }}
              >
                <EditIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
          );
        },
      },
    ]
      .filter((col) => col.key !== "actions" || canUpdate)
      // headerLabel is what the column picker lists; it always matches the header.
      .map((col) => ({ ...col, headerLabel: col.title })),
    [canUpdate, locationLevels]
  );

  // Columns the picker offers. User and Actions are `fixed`, which the picker
  // treats as always-on and not deselectable.
  const selectableColumnKeys = useMemo(
    () => columns.filter((col) => col.fixed === undefined).map((col) => col.dataIndex),
    [columns]
  );
  const selectableColumnSignature = selectableColumnKeys.join("|");

  // Seed the selection from the stored preference. Columns the admin hid stay
  // hidden; columns that did not exist when they chose (a location level added to
  // the org since) are switched on, so a new dimension is not invisible forever.
  useEffect(() => {
    if (!selectableColumnKeys.length) return;
    const stored = AppCache.getItem(COLUMNS_CACHE_KEY);
    if (!stored || !Array.isArray(stored.selected) || !Array.isArray(stored.known)) {
      setSelectedColumns(selectableColumnKeys);
      return;
    }
    const kept = stored.selected.filter((key) => selectableColumnKeys.includes(key));
    const appeared = selectableColumnKeys.filter((key) => !stored.known.includes(key));
    // Normalise to the canonical column order: the stored order then stays stable,
    // and the picker's order-sensitive Reset/dirty comparisons see what they expect.
    // (The picker lists columns alphabetically within fixed/selected/unselected
    // buckets, so this is not the order it displays.)
    const chosen = new Set([...kept, ...appeared]);
    setSelectedColumns(selectableColumnKeys.filter((key) => chosen.has(key)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableColumnSignature]);

  const applySelectedColumns = (newSelectedColumns) => {
    const chosen = new Set(newSelectedColumns);
    // The picker hands back its own order and may include the fixed columns;
    // normalise to the canonical order and to selectable columns only.
    const ordered = selectableColumnKeys.filter((key) => chosen.has(key));
    const stored = AppCache.getItem(COLUMNS_CACHE_KEY);
    AppCache.setItem(COLUMNS_CACHE_KEY, {
      selected: ordered,
      // Union rather than replace. The picker is live from the first paint, so a
      // change applied before the list request resolves would otherwise write a
      // `known` with no location-level keys, forgetting that a level column had
      // been hidden on purpose and resurrecting it on the next load.
      known: [
        ...new Set([
          ...(Array.isArray(stored?.known) ? stored.known : []),
          ...selectableColumnKeys,
        ]),
      ],
    });
    setSelectedColumns(ordered);
  };

  // What the table actually renders, and what the CSV exports. A fixed column is
  // always present - the picker shows it checked and greyed out. Before the effect
  // above has seeded the selection, every column shows: that is the default, and
  // starting from the empty list would paint a one-column table for a frame.
  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (col) =>
          col.fixed !== undefined ||
          selectedColumns === null ||
          selectedColumns.includes(col.dataIndex)
      ),
    [columns, selectedColumns]
  );
  const visibleColumnKeys = useMemo(
    () => visibleColumns.map((col) => col.dataIndex),
    [visibleColumns]
  );
  // "Reset" in the picker restores every column, which is also the initial state.
  const defaultSelectedColumns = useMemo(
    () => columns.map((col) => col.dataIndex),
    [columns]
  );

  // The shared table keys rows by `id` (falls back to `key`); RBAC users carry `userId`.
  const tableData = useMemo(() => users.map((u) => ({ ...u, id: u.userId })), [users]);

  const emptyMessage = search
    ? `No users found for "${search}"`
    : selectedRoleCodes.length
    ? "No users found for the selected role"
    : "No users found";

  // Show full-page edit view when a user is selected
  if (editUser) {
    return (
      <UserEditDialog
        user={editUser}
        onBack={() => setEditUser(null)}
        onSuccess={() => {
          setEditUser(null);
          fetchUsers(buildParams());
        }}
      />
    );
  }

  // Actions live inside the table header (search + buttons), mirroring the
  // Supported Locations page so the search styling/placement is uniform.
  const tableHeaderActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 320,
          "& .MuiInputBase-input": { paddingTop: "8px", paddingBottom: "8px" },
        }}
      >
        <SearchInput
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search users..."
        />
      </Box>
      {canUpdate && selectedRowKeys.length > 0 && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setBulkPlan("paid")}
            data-testid="bulk-set-paid-btn"
            sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px", whiteSpace: "nowrap", color: PRIMARY_PURPLE, borderColor: PRIMARY_PURPLE, "&:hover": { borderColor: "#3D35C0", backgroundColor: "#EEEFFF" } }}
          >
            {`Set Paid (${selectedRowKeys.length})`}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setBulkPlan("free")}
            data-testid="bulk-set-free-btn"
            sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px", whiteSpace: "nowrap", color: "#4B5563", borderColor: "#9CA3AF", "&:hover": { borderColor: "#4B5563", backgroundColor: "#F3F4F6" } }}
          >
            {`Set Free (${selectedRowKeys.length})`}
          </Button>
        </>
      )}
      <Button
        variant="outlined"
        size="small"
        onClick={handleDownloadUsers}
        disabled={isDownloading}
        data-testid="download-users-btn"
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: 13,
          borderRadius: "10px",
          whiteSpace: "nowrap",
          color: PRIMARY_PURPLE,
          borderColor: PRIMARY_PURPLE,
          "&:hover": { borderColor: "#3D35C0", backgroundColor: "#EEEFFF" },
        }}
      >
        {isDownloading ? "Downloading…" : "Download"}
      </Button>
      <CanCreate feature={FEATURE_CODES.ACCESS_MANAGEMENT}>
        <Button
          variant="contained"
          size="small"
          onClick={() => setCreateUserOpen(true)}
          data-testid="create-user-btn"
          sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px", backgroundColor: PRIMARY_PURPLE, "&:hover": { backgroundColor: "#3D35C0" } }}
        >
          + Create New User
        </Button>
      </CanCreate>
      <CanUpdate feature={FEATURE_CODES.ACCESS_MANAGEMENT}>
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={() => setLogoutAllOpen(true)}
          data-testid="force-logout-all-btn"
          sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px", whiteSpace: "nowrap" }}
        >
          Force Logout All
        </Button>
      </CanUpdate>
    </Box>
  );

  return (
    <Box>
      {/* Sub-header: subtitle (actions moved into the table header) */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 14, color: "#888" }}>
          Assign roles, locations, and customer accounts to users
        </Typography>
      </Box>

      <GenericDraggableTableView
        count={baseTotal}
        filteredCount={search || selectedRoleCodes.length ? total : 0}
        showPrimaryButton={false}
        disableSearch={true}
        disableDownloads={true}
        disableColumnSettings={false}
        customRightActions={true}
        customRightActionsView={tableHeaderActions}
        filters={availableFilters}
        selectedFilters={selectedFilters}
        applyFilters={applyFilters}
        tableColumns={visibleColumns}
        initialColumns={columns}
        selectedColumns={visibleColumnKeys}
        applySelectedColumns={applySelectedColumns}
        // No artificial cap: every column here is one an admin may legitimately
        // need, and the default is all of them.
        maxColumnsAllowed={columns.length}
        defaultSelectedColumns={defaultSelectedColumns}
        tableData={tableData}
        isLoading={loading}
        pageNumber={page}
        rowsPerPage={perPage}
        total={total}
        applySort={() => {}}
        onPageChange={(pageNo) => setPage(pageNo)}
        emptyMessage={emptyMessage}
        rowSelection={
          canUpdate
            ? {
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                preserveSelectedRowKeys: true,
              }
            : undefined
        }
      />

      {/* Create new user dialog */}
      <CreateUserDialog
        open={createUserOpen}
        onClose={() => setCreateUserOpen(false)}
        onSuccess={() => {
          setCreateUserOpen(false);
          fetchUsers(buildParams());
          fetchBaseTotal();
        }}
      />

      {/* Single user force logout dialog */}
      <Dialog open={Boolean(logoutUser)} onClose={() => !loggingOut && setLogoutUser(null)}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>Force Logout User?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            This will expire all active sessions for <strong>{logoutUser?.name}</strong>.
            They will be required to log in again.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setLogoutUser(null)} disabled={loggingOut} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleForceLogout}
            disabled={loggingOut}
            sx={{ textTransform: "none" }}
          >
            {loggingOut ? "Logging out…" : "Force Logout"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logout all users dialog */}
      <Dialog open={logoutAllOpen} onClose={() => !loggingOutAll && setLogoutAllOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>Force Logout All Users?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            This will expire all active sessions for <strong>every user in your organisation</strong>.
            Everyone will be required to log in again.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setLogoutAllOpen(false)} disabled={loggingOutAll} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleForceLogoutAll}
            disabled={loggingOutAll}
            sx={{ textTransform: "none" }}
          >
            {loggingOutAll ? "Logging out…" : "Force Logout All"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single user plan change dialog */}
      <Dialog open={Boolean(planChange)} onClose={() => !savingPlan && setPlanChange(null)}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>Change Subscription Plan?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            Set <strong>{planChange?.user?.name}</strong> to the{" "}
            <strong>{planChange?.plan === "paid" ? "Paid" : "Free"}</strong> plan.
            {planChange?.plan === "paid"
              ? " Paid users are blocked from logging in when the org subscription is expired."
              : " Free users can always log in, regardless of the org subscription."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setPlanChange(null)} disabled={savingPlan} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdatePlan}
            disabled={savingPlan}
            data-testid="confirm-plan-change-btn"
            sx={{ textTransform: "none", backgroundColor: PRIMARY_PURPLE, "&:hover": { backgroundColor: "#3D35C0" } }}
          >
            {savingPlan ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk plan change dialog */}
      <Dialog open={Boolean(bulkPlan)} onClose={() => !savingPlan && setBulkPlan(null)}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>Change Plan for Selected Users?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            Set <strong>{selectedRowKeys.length}</strong> selected user
            {selectedRowKeys.length !== 1 ? "s" : ""} to the{" "}
            <strong>{bulkPlan === "paid" ? "Paid" : "Free"}</strong> plan.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setBulkPlan(null)} disabled={savingPlan} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkUpdatePlan}
            disabled={savingPlan}
            data-testid="confirm-bulk-plan-change-btn"
            sx={{ textTransform: "none", backgroundColor: PRIMARY_PURPLE, "&:hover": { backgroundColor: "#3D35C0" } }}
          >
            {savingPlan ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserAssignmentsTab;
